import { useMemo } from "react";
import { trpc, type RouterOutputs } from "../trpc";
import { useUserAccountRole } from "../permissions/useUserAccountRole";

type Guards = RouterOutputs["guard"]["getGuards"];

const EMPTY_GUARDS: never[] = [];

export const MANAGED_GUARDS_INPUT = {
  guardType: "managed_auth_only",
  isActive: true,
} as const;

// Guard selectedApis are versionless (e.g. "GoogleMailV2CLIAPI@1.0.0" ->
// "GoogleMailV2CLIAPI").
function stripVersion(appId: string) {
  return appId?.split("@")[0] ?? "";
}

function coversApp(guard: Guards[number], strippedId: string) {
  return (
    guard.selectedApis === "all" || guard.selectedApis.includes(strippedId)
  );
}

/** Builds an `isAppManaged(appId)` check: any managed_auth_only guard covers the app. */
export function buildIsAppManaged(guards: Guards | undefined) {
  const list = guards ?? EMPTY_GUARDS;
  return (appId: string) => {
    const strippedId = stripVersion(appId);
    return strippedId
      ? list.some((guard) => coversApp(guard, strippedId))
      : false;
  };
}

/** Mirrors the backend guard: share-only overrides and owner/super admin exemptions allow creates. */
export function buildIsAppCreateRestricted(
  guards: Guards | undefined,
  { isOwnerOrSuperAdmin = false }: { isOwnerOrSuperAdmin?: boolean } = {},
) {
  const list = guards ?? EMPTY_GUARDS;
  return (appId: string) => {
    const strippedId = stripVersion(appId);
    if (!strippedId) return false;
    return list.some((guard) => {
      if (!coversApp(guard, strippedId)) return false;
      if (guard.exemptOwnerAndSuperadmin && isOwnerOrSuperAdmin) return false;
      const operations = guard.perAppOperations?.[strippedId];
      return !operations || operations.includes("create");
    });
  };
}

// isAppManaged feeds the compliance label and is never flag-gated; create blocking
// uses isAppCreateRestricted plus the backend splits (see useConnectionCreateRules).
export function useQueryManagedGuards() {
  const { data, isLoading, isError } = trpc.guard.getGuards.useQuery(
    MANAGED_GUARDS_INPUT,
    {
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
  );

  const { isAccountOwner, isSuperAdmin } = useUserAccountRole();
  const isOwnerOrSuperAdmin = isAccountOwner || isSuperAdmin;

  const isAppManaged = useMemo(() => buildIsAppManaged(data), [data]);
  const isAppCreateRestricted = useMemo(
    () => buildIsAppCreateRestricted(data, { isOwnerOrSuperAdmin }),
    [data, isOwnerOrSuperAdmin],
  );

  return {
    isAppManaged,
    isAppCreateRestricted,
    isLoading,
    isError,
  };
}
